// 頁型模板⑤：登入頁（1 筆 route：pageId `index`）。
//
// 原系統對應：`WEB-INF/views/index.jsp`（132 行，批次 25 #27 對齊）。
//   :63      `<body class="login-background">` —— **沒有 decorator、沒有側邊選單、沒有頂列**，
//            所以這一頁在路由層是掛在 AppLayout **外面**的（見 src/app/router.tsx），
//            本模板自帶整頁版面（左欄公告＋右側登入卡）且自帶 <DialogProvider>
//            （AppLayout 的 DialogProvider 不涵蓋登入頁）。
//   :69      `<%@ include file="announceModal.jsp" %>` —— 左欄公告欄＋公告 modal 群
//            （細節與行號見 LoginAnnouncements.tsx 檔頭）。
//   :75      `.middle-box.text-center.loginscreen` 置中的登入卡（col-md-3 右欄）
//   :79      `<h3>${sysTitle}</h3>`（EL 值原始碼看不到，改用同頁 <title> 的字面值；
//            panel-heading-levels PANEL_HEADING_LEVEL_OVERRIDES.index = 3）
//   :81-82   form action="login"（POST；雛形不接＝auth seam，D25-1）
//   :83      anti-CSRF hidden（**不畫**＝Q2 裁決：雛形沒有 session／CSRF 機制）
//   :84-85   annoId（value="x"）／goToUrl hidden（URL 參數鏈有真行為，照畫＋鏡像）
//   :87-92   單位下拉 `select[name=hosp]`（外部AD登入 ldap :90／本機帳號 Z :91；?hosp= 預選）
//   :96-101  帳號 `input[name=loginName]` placeholder「請輸入您的帳號」required maxlength=20（input :99-100）
//   :103-110 密碼 `input[name=loginPin]` placeholder「請輸入您的密碼」autocomplete=off
//            required maxlength=100（input :106-108）
//   :112-116 `<button ...>登入</button>`（button :114-116，type=submit）
//   :119-121 `#loginAnncDiv`（getAnnouncements 第一則 subject——indexAnnounce.js:115-119 原始 subject＋<strong>、無 click 綁定＝死鈕，React 照實渲染不開 modal）
//   :122-124 `<p id="message">` —— 失敗原因由後端以 `?failReason=` 帶回＋`?annoId=` 帶回
//            「請先登入系統」（js-my/index.js:5-21（failReason :5-11／annoId 請先登入系統 :14-21）；雛形 error scenario 文案維持 t('error.generic')）
//   :126     版權列（版權所有 **勤紘科技** 臨床教育訓練管理系統 © 2019）
//
// URL 參數鏈（js-my/index.js:5-36）：
//   failReason → #message 文字（:5-9）；annoId → #message「請先登入系統」＋showAlertDialog（:14-21）；
//   goToUrl → 含 http/https/:// ＝非法轉址（不設值），否則 #goToUrl=val（:24-34）；
//   hosp → #hosp 預選（:35-36；值不在選項內＝不選，jQuery .val() 對不存在 option 的語意【雛形值待實機校準】）。
//
// 支援的 scenario（§5.2）：default / validation-error / error
//
// 【已知原系統缺陷】必填檢核完全靠 HTML5 `required`，訊息由瀏覽器產生、隨語系不同，
// 且不會標在欄位旁。雛形改成畫面上的欄位級訊息（t('validation.required')，屬【雛形新增】文案），
// 模組 Markdown 的「已知原系統缺陷」欄要記這一條。
//
// 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】

import { type FormEvent, type ReactElement, useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { DialogProvider, useDialogs } from '@/components/layouts/dialogs/DialogProvider';
import { Button } from '@/components/base/Button';
import { FormMessage } from '@/components/base/FormMessage';
import { PasswordInput } from '@/components/base/PasswordInput';
import { Select } from '@/components/base/Select';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, FormGrid, FormGridField } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { useList, useScenario, useSession } from '@/mocks';
import { readScenarioFromSearch } from '@/mocks/scenario-injection';
import { legacyAlertFallback } from './page-behaviors';
import {
  AnnounceDetailModal,
  AnnounceListModal,
  LoginAnnouncementPanel,
} from './LoginAnnouncements';
import type { PageTemplateProps } from './types';

/** WEB-INF/views/index.jsp:90-91 的兩個 option（外層 <select> 在 :89、:92），value 照原樣保留。 */
const UNIT_OPTIONS = [
  { value: 'ldap', label: t('login.unitLdap') },
  { value: 'Z', label: t('login.unitLocal') },
] as const;

export function LoginPage({ route }: PageTemplateProps): ReactElement {
  // 登入頁掛在 AppLayout 外（無全站 DialogProvider）——自帶一份（indexAnnounce 失敗 alert／
  // annoId 請先登入系統 alert 都走同一組對話框）。
  return (
    <DialogProvider>
      <LoginPageInner route={route} />
    </DialogProvider>
  );
}

function LoginPageInner({ route }: PageTemplateProps): ReactElement {
  const pageId = route.pageId;
  const scenario = useScenario();
  const { setRole } = useSession();
  const navigate = useNavigate();
  const dialogs = useDialogs();

  // 公告清單（indexAnnounce.js:101-152 getAnnouncements → login/getAnnouncementList_newAjax :104；
  // endpoint-map list:index→/login/getAnnouncementListAjax 兩支同邏輯，path 差異＝D25-5）。
  const announce = useList(pageId, { pageSize: 50 });
  const announceRows = announce.data?.rows ?? [];

  // URL 參數（js-my/index.js:5-36；window.location.search＝雛形唯一帶參管道）。
  const params = new URLSearchParams(
    typeof window === 'undefined' ? '' : window.location.search,
  );
  const failReason = params.get('failReason');
  const annoId = params.get('annoId');
  // goToUrl 非法轉址（index.js:24-34：含 https/http/:// 不設值；否則 #goToUrl=val）。
  const goToUrlParam = params.get('goToUrl') ?? '';
  const goToUrlValue =
    goToUrlParam !== '' &&
    !goToUrlParam.includes('https') &&
    !goToUrlParam.includes('http') &&
    !goToUrlParam.includes('://')
      ? goToUrlParam
      : '';
  // hosp 預選（index.js:35-36；值不在選項內＝維持預設）。
  const hospParam = params.get('hosp');
  const hospInitial = UNIT_OPTIONS.some((option) => option.value === hospParam)
    ? (hospParam as string)
    : UNIT_OPTIONS[0].value;

  // 公告 modal 狀態機（index.js:56-73 modal_size：清單 show→1 :72-73／單則 show（1→2）並藏清單 :61-65／
  // 單則 hide（2→重開清單）:56-59／清單 hide（1→0）:67-70）。
  const [listModalOpen, setListModalOpen] = useState(false);
  const [detailIndex, setDetailIndex] = useState<number | null>(null);
  const [modalSize, setModalSize] = useState(0);

  const [unit, setUnit] = useState<string>(hospInitial);
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (account.trim() === '') {
      next.account = t('validation.required');
    }
    if (password === '') {
      next.password = t('validation.required');
    }
    return next;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length === 0) {
      // Determine role based on account prefix (ui-admin, ui-teacher, ui-student)
      let role: 'admin' | 'teacher' | 'student' = 'student';
      const acc = account.trim().toLowerCase();
      if (acc.includes('admin')) role = 'admin';
      else if (acc.includes('teacher')) role = 'teacher';
      else if (acc.includes('student')) role = 'student';
      // Update session role
      setRole(role);
      // Navigate to role‑specific home page
      const homePath = role === 'admin' ? '/main' : role === 'teacher' ? '/main/teacherMain' : '/main/studentMain';
      navigate(homePath, { replace: true });
    }
  }

  // ?annoId= → #message「請先登入系統」＋showAlertDialog（index.js:14-21）。
  useEffect(() => {
    if (annoId !== null) {
      void dialogs.alert('請先登入系統');
    }
    // 只看初始 URL（legacy 也是 ready 時讀一次）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 公告查詢失敗 → showAlertDialog(response.desc)（indexAnnounce.js:147-149）。
  useEffect(() => {
    if (announce.error !== null) {
      void dialogs.alert(legacyAlertFallback(announce.error));
    }
  }, [announce.error, dialogs]);

  // scenario 預設集：validation-error 直接停在「兩個欄位都沒填」的錯誤狀態。
  useEffect(() => {
    if (scenario === 'validation-error') {
      setErrors({ account: t('validation.required'), password: t('validation.required') });
    }
  }, [scenario]);

  // FC-5 R2（C1-a）：legacy 對「已登入使用者造訪登入頁」一律導去首頁（三角色 raw DOM 的
  // `index` 頁全部判 redirect——擷取用的是已登入的 storageState，見 design-R0.md §A.1(1)）。
  // 雛形沒有真正的伺服器端 session，但 URL query 的 `role` 參數本來就代表「這次造訪帶著
  // 已登入的角色身分」（§16.2 demo 用途／視覺回歸擷取共用同一個入口，見
  // `scenario-injection.ts`）。帶 role 參數造訪 `/index` 時視為已登入，對齊 legacy 直接
  // 導去首頁；沒帶參數的一般造訪（單元測試、fields/actions 維度的契約抽取）不受影響，
  // 繼續渲染登入表單——這兩種造訪本來就是不同語意，不是同一件事情的兩個分支。
  const hasRoleQuery =
    typeof window !== 'undefined' &&
    readScenarioFromSearch(window.location.search).role !== undefined;
  if (hasRoleQuery) {
    return <Navigate to="/main" replace />;
  }

  function openList(): void {
    setListModalOpen(true);
    setModalSize(1);
  }

  function openDetail(index: number): void {
    setModalSize((prev) => (prev === 1 ? 2 : prev));
    setDetailIndex(index);
    setListModalOpen(false);
  }

  function closeList(): void {
    setListModalOpen(false);
    setModalSize((prev) => (prev === 1 ? 0 : prev));
  }

  function closeDetail(): void {
    const reopenList = modalSize === 2;
    setDetailIndex(null);
    if (reopenList) {
      setListModalOpen(true);
      setModalSize(1);
    }
  }

  return (
    <div
      data-ui-id={pageId}
      className="flex min-h-screen items-start justify-center gap-6 bg-surface-subtle px-4 py-10 font-sans text-ink"
    >
      {/* 左欄公告欄（index.jsp:69 include announceModal.jsp，col-md-9；細節 LoginAnnouncements.tsx；窄版垂直堆疊仍可見＝D25-4 近似）。 */}
      <div className="w-full max-w-[620px]">
        <LoginAnnouncementPanel
          pageId={pageId}
          rows={announceRows}
          onMore={openList}
          onOpen={openDetail}
        />
      </div>
      {/* 右欄登入卡（col-md-3）。 */}
      <div data-ui-id={`${pageId}-login-box`} className="w-full max-w-[420px]">
        <ContentPanel
          uiId={`${pageId}-login-panel`}
          title={t('login.systemTitle')}
          headingLevel={panelHeadingLevelFor(pageId)}
        >
          <form
            data-ui-id={`${pageId}-login-form`}
            // 批次 44（#42）：action/method 鏡像（index.jsp login form action="/login" method="POST"）。
            action="/login"
            method="POST"
            onSubmit={handleSubmit}
            className="flex flex-col gap-3"
          >
            {/* hidden（index.jsp:83-85）：anti-CSRF（:83）／csrfToken 不畫＝Q2 裁決（雛形無 session）；
                annoId/goToUrl 有 URL 參數行為，照畫。 */}
            <input
              type="hidden"
              name="annoId"
              value={annoId ?? 'x'}
              data-ui-id={`${pageId}-login-hidden-anno-id`}
            />
            <input
              type="hidden"
              name="goToUrl"
              value={goToUrlValue}
              data-ui-id={`${pageId}-login-hidden-go-to-url`}
            />
            <FormGrid uiId={`${pageId}-login-grid`} columns={1}>
              <FormGridField uiId={`${pageId}-login-field-unit`} label={t('login.unit')} required={false}>
                <Select
                  uiId={`${pageId}-login-input-unit`}
                  name="hosp"
                  value={unit}
                  onChange={setUnit}
                  options={UNIT_OPTIONS.map((option) => ({ ...option }))}
                />
              </FormGridField>
              {/* 批次 50（工作項 3）：FormGridField 不傳 required＝不畫星號 span——legacy
                  index.jsp 登入欄 label 為 <span>帳號</span>/<span>密碼</span>（無星號，僅
                  input required 屬性）＝b49-triage 案 4 M3「react 過度裝飾」；控制項的
                  required 屬性與驗證行為保留（invalid/error 鏈不動）。unit 欄批次 44
                  已同款（:256 required=false）。 */}
              <FormGridField
                uiId={`${pageId}-login-field-account`}
                label={t('login.account')}
                {...(errors.account === undefined ? {} : { error: errors.account })}
              >
                <TextInput
                  uiId={`${pageId}-login-input-account`}
                  name="loginName"
                  value={account}
                  onChange={setAccount}
                  placeholder={t('login.accountPlaceholder')}
                  invalid={errors.account !== undefined}
                  required
                  maxLength={20}
                />
              </FormGridField>
              <FormGridField
                uiId={`${pageId}-login-field-password`}
                label={t('login.password')}
                {...(errors.password === undefined ? {} : { error: errors.password })}
              >
                <PasswordInput
                  uiId={`${pageId}-login-input-password`}
                  name="loginPin"
                  value={password}
                  onChange={setPassword}
                  placeholder={t('login.passwordPlaceholder')}
                  invalid={errors.password !== undefined}
                  required
                  maxLength={100}
                  autoComplete="off"
                />
              </FormGridField>
            </FormGrid>

            <Button uiId={`${pageId}-login-submit`} variant="primary" size="md" type="submit">
              {t('action.login')}
            </Button>

            {/* #loginAnncDiv（index.jsp:119-121；getAnnouncements 第一則 subject——legacy 無 click
                綁定＝死鈕照實（indexAnnounce.js:115-119 原始 subject＋<strong> 不截斷）；
                React 渲染為無 onClick 的 strong，不開 modal（D25-1 同族照實）。 */}
            {announceRows.length > 0 ? (
              <strong
                data-ui-id={`${pageId}-login-annc-first`}
                className="text-ink"
              >
                {String(announceRows[0]!['subject'] ?? '')}
              </strong>
            ) : null}
          </form>

          {/* <p id="message">（index.jsp:122-124，在 form 外）：failReason／annoId 文字；
              error scenario 維持雛形通用錯誤文案。 */}
          <div data-ui-id={`${pageId}-login-message`} className="flex flex-col gap-1">
            {failReason !== null ? (
              <FormMessage uiId={`${pageId}-login-message-fail`} tone="error">
                {failReason}
              </FormMessage>
            ) : null}
            {annoId !== null ? (
              <FormMessage uiId={`${pageId}-login-message-anno`} tone="error">
                請先登入系統
              </FormMessage>
            ) : null}
            {scenario === 'error' ? (
              <FormMessage uiId={`${pageId}-login-message-error`} tone="error">
                {t('error.generic')}
              </FormMessage>
            ) : null}
          </div>
        </ContentPanel>
        <p
          data-ui-id={`${pageId}-login-copyright`}
          className="mt-3 text-center text-ink-muted text-xs"
        >
          {t('login.copyright')}
        </p>
      </div>
      {/* 公告 modal 群（announceModal.jsp:36-125；modal_size 狀態機 index.js:56-73）。 */}
      {listModalOpen ? (
        <AnnounceListModal pageId={pageId} rows={announceRows} onOpen={openDetail} onClose={closeList} />
      ) : null}
      {detailIndex !== null && announceRows[detailIndex] !== undefined ? (
        <AnnounceDetailModal pageId={pageId} row={announceRows[detailIndex]!} onClose={closeDetail} />
      ) : null}
    </div>
  );
}
